import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Upload, X, ImagePlus, GripVertical, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Sparkles, Loader2, Mail, Unlink, RefreshCw } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useServerFn } from "@tanstack/react-start";
import { generateCarDescription } from "@/lib/car-description.functions";
import { pingSearchEnginesForCarStatus } from "@/lib/status-ping.functions";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { validateImageFiles } from "@/lib/image-utils";
import { uploadCarImage, DERIVATIVE_FORMATS, DERIVATIVE_WIDTHS, formatBytes } from "@/lib/car-image-pipeline";
import { toast } from "sonner";
import { BulkImageUploadDialog } from "@/components/BulkImageUploadDialog";
import { CarSeoEditor, type SeoValidationResult } from "@/components/admin/CarSeoEditor";
import { AiaChecklist } from "@/components/admin/AiaChecklist";
import { CarImageManifestStatus } from "@/components/admin/CarImageManifestStatus";
import type { AiaChecklistResult } from "@/lib/aia-checklist";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  status: fallback(z.enum(["all", "active", "draft", "sold", "reserved"]), "all").default("all"),
  source: fallback(z.enum(["all", "manual", "bilinfo"]), "all").default("all"),
  demo: fallback(z.enum(["all", "demo", "real"]), "all").default("all"),
  images: fallback(z.enum(["all", "with", "without"]), "all").default("all"),
  page: fallback(z.number().int().min(1), 1).default(1),
  pageSize: fallback(z.union([z.literal(10), z.literal(20), z.literal(50), z.literal(100)]), 20).default(20),
  sortKey: fallback(z.enum(["brand", "year", "price", "status", "inquiries"]), "inquiries").default("inquiries"),
  sortDir: fallback(z.enum(["asc", "desc"]), "desc").default("desc"),
  // Optional deep-link target: when present, the page will scroll to and
  // auto-open the edit dialog for the matching car id once cars are loaded.
  // Used by e.g. the image-health report on /admin/sync to jump straight
  // into a specific vehicle.
  editId: fallback(z.string().uuid().optional(), undefined).default(undefined),
});

export const Route = createFileRoute("/admin/biler")({
  // Disable per-route code splitting: the TanStack code-splitter mis-parses
  // this file (Babel reports "Unexpected reserved word 'await'" around the
  // async handlers in the component body). Bundling everything together
  // sidesteps the splitter transform.
  codeSplitGroupings: [],
  validateSearch: zodValidator(searchSchema),
  component: AdminCars,
});

type CarForm = {
  brand: string;
  model: string;
  variant: string;
  year: number;
  mileage: number;
  price: number;
  fuel_type: string;
  transmission: string;
  color: string;
  body_type: string;
  doors: number;
  seats: number;
  engine_size: string;
  horsepower: number;
  description: string;
  equipment: string;
  status: string;
  is_new: boolean;
  is_popular: boolean;
  is_leasing: boolean;
  is_demo: boolean;
  leasing_price: number;
  first_registration: string;
  vin_number: string;
  license_plate: string;
  gtin: string;
  seo_title: string;
  seo_description: string;
  seo_slug: string;
  images: string[];
};

const emptyForm: CarForm = {
  brand: "", model: "", variant: "", year: new Date().getFullYear(), mileage: 0, price: 0,
  fuel_type: "benzin", transmission: "automatik", color: "", body_type: "", doors: 4, seats: 5,
  engine_size: "", horsepower: 0, description: "", equipment: "", status: "draft",
  is_new: false, is_popular: false, is_leasing: false, is_demo: false, leasing_price: 0, first_registration: "",
  vin_number: "", license_plate: "", gtin: "", seo_title: "", seo_description: "", seo_slug: "", images: [],
};

function AdminCars() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [bulkImagesOpen, setBulkImagesOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<CarForm>(emptyForm);
  const [csvText, setCsvText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [convertTarget, setConvertTarget] = useState<{ id: string; label: string } | null>(null);
  const [seoValidation, setSeoValidation] = useState<SeoValidationResult>({ hasErrors: false, hasWarnings: false });
  const [aiaResult, setAiaResult] = useState<AiaChecklistResult | null>(null);
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = urlSearch.q;
  const statusFilter = urlSearch.status;
  const sourceFilter = urlSearch.source;
  const demoFilter = urlSearch.demo;
  const imagesFilter = urlSearch.images;
  const page = urlSearch.page;
  const PAGE_SIZE = urlSearch.pageSize;
  const sort = useMemo(
    () => (urlSearch.sortKey ? { key: urlSearch.sortKey, dir: urlSearch.sortDir } : null),
    [urlSearch.sortKey, urlSearch.sortDir],
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [descTone, setDescTone] = useState<"sælgende" | "neutral" | "luksus">("sælgende");
  const generateDescFn = useServerFn(generateCarDescription);
  const pingStatusFn = useServerFn(pingSearchEnginesForCarStatus);
  type SortKey = "brand" | "year" | "price" | "status" | "inquiries";

  type SearchState = z.infer<typeof searchSchema>;
  const setSearch = (q: string) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, q, page: 1 }), replace: true });
  };
  const setStatusFilter = (status: "all" | "active" | "draft" | "sold" | "reserved") => {
    navigate({ search: (prev: SearchState) => ({ ...prev, status, page: 1 }), replace: true });
  };
  const setSourceFilter = (source: "all" | "manual" | "bilinfo") => {
    navigate({ search: (prev: SearchState) => ({ ...prev, source, page: 1 }), replace: true });
  };
  const setDemoFilter = (demo: "all" | "demo" | "real") => {
    navigate({ search: (prev: SearchState) => ({ ...prev, demo, page: 1 }), replace: true });
  };
  const setImagesFilter = (images: "all" | "with" | "without") => {
    navigate({ search: (prev: SearchState) => ({ ...prev, images, page: 1 }), replace: true });
  };
  const setPage = (updater: number | ((p: number) => number)) => {
    navigate({
      search: (prev: SearchState) => ({
        ...prev,
        page: typeof updater === "function" ? updater(prev.page) : updater,
      }),
      replace: true,
    });
  };
  const setPageSize = (pageSize: 10 | 20 | 50 | 100) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, pageSize, page: 1 }), replace: true });
  };
  const toggleSort = (key: SortKey) => {
    navigate({
      search: (prev: SearchState): SearchState => {
        const prevKey = prev.sortKey;
        const prevDir = prev.sortDir;
        // For "inquiries" we want most-interest-first on the initial click,
        // so flip the default direction order: desc → asc → off.
        const initialDir: "asc" | "desc" = key === "inquiries" ? "desc" : "asc";
        const secondDir: "asc" | "desc" = key === "inquiries" ? "asc" : "desc";
        if (prevKey !== key) return { ...prev, sortKey: key, sortDir: initialDir, page: 1 };
        if (prevDir === initialDir) return { ...prev, sortKey: key, sortDir: secondDir, page: 1 };
        return { ...prev, sortKey: undefined, sortDir: "asc", page: 1 };
      },
      replace: true,
    });
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    fileIndex: number;
    fileTotal: number;
    derivative: string;
  } | null>(null);

  const uploadImages = useCallback(async (files: FileList | File[]) => {
    // The new pipeline writes manifest rows tied to a car_id, so the car
    // must already exist in the database. For brand-new cars, the admin
    // needs to save first (creates the row) and then re-open to upload.
    if (!editId) {
      toast.error("Gem bilen først", {
        description: "Billed-pipelinen kræver at bilen er oprettet i databasen før upload (så manifest-rækkerne kan kobles på).",
      });
      return;
    }

    // Reject anything that would later break the Google/Meta feeds
    // (>16 MB or non-raster MIME types) before we touch storage.
    const { accepted, rejected } = validateImageFiles(files);
    if (rejected.length > 0) {
      const first = rejected[0].error.message;
      toast.error(
        rejected.length === 1
          ? "Billede afvist"
          : `${rejected.length} billeder afvist`,
        { description: first + (rejected.length > 1 ? ` (+${rejected.length - 1} mere)` : "") },
      );
    }
    if (accepted.length === 0) return;

    setUploading(true);
    // RLS requires uploads into a folder named after the uploader's user_id.
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData.user?.id;
    if (!userId) {
      toast.error("Du skal være logget ind for at uploade billeder");
      setUploading(false);
      return;
    }

    const newUrls: string[] = [];
    let totalDerivativeBytes = 0;
    let totalOriginalBytes = 0;
    let firstUpload = (form.images?.length ?? 0) === 0;

    for (let i = 0; i < accepted.length; i++) {
      const file = accepted[i];
      try {
        const result = await uploadCarImage(file, {
          carId: editId,
          userId,
          markFirstAsPrimary: firstUpload && i === 0,
          onDerivativeProgress: ({ format, width }) =>
            setUploadProgress({
              fileIndex: i + 1,
              fileTotal: accepted.length,
              derivative: `${format.toUpperCase()} ${width}w`,
            }),
        });
        newUrls.push(result.canonicalUrl);
        totalDerivativeBytes += result.totalDerivativeBytes;
        totalOriginalBytes += result.originalBytes;
        firstUpload = false;
      } catch (err) {
        console.error(`Pipeline failed for ${file.name}:`, err);
        toast.error(`Billede fejlede: ${file.name}`, {
          description: err instanceof Error ? err.message : "Ukendt fejl",
        });
      }
    }

    setForm((prev) => ({ ...prev, images: [...prev.images, ...newUrls] }));
    setUploading(false);
    setUploadProgress(null);

    if (newUrls.length > 0) {
      const derivativesPerImage = DERIVATIVE_WIDTHS.length * DERIVATIVE_FORMATS.length;
      toast.success(
        `${newUrls.length} billede${newUrls.length > 1 ? "r" : ""} uploadet (${newUrls.length * derivativesPerImage} derivater)`,
        {
          description: `Original ${formatBytes(totalOriginalBytes)} → derivater ${formatBytes(totalDerivativeBytes)} • AVIF/WebP/JPG @ 960/1280/1920`,
        },
      );
    }
  }, [editId, form.images?.length]);

  const removeImage = (index: number) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const moveImage = (from: number, to: number) => {
    setForm((prev) => {
      const imgs = [...prev.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...prev, images: imgs };
    });
  };

  const { data: cars, isLoading } = useQuery({
    queryKey: ["admin-cars"],
    queryFn: async () => {
      const { data } = await supabase.from("cars").select("*").order("created_at", { ascending: false });
      const carRows = data || [];
      const ids = carRows.map((c) => c.id);
      const { data: identifiers } = ids.length
        ? await supabase.from("car_identifiers").select("car_id, vin_number, license_plate, gtin").in("car_id", ids)
        : { data: [] as { car_id: string; vin_number: string | null; license_plate: string | null; gtin: string | null }[] };
      const idMap = new Map((identifiers ?? []).map((r) => [r.car_id, r]));
      return carRows.map((c) => ({
        ...c,
        vin_number: idMap.get(c.id)?.vin_number ?? null,
        license_plate: idMap.get(c.id)?.license_plate ?? null,
        gtin: idMap.get(c.id)?.gtin ?? null,
      }));
    },
  });

  // Count inquiries (contact_messages) per car + track latest one,
  // so admin sees both interest volume and how recent the activity is.
  const { data: inquiryStats } = useQuery({
    queryKey: ["admin-car-inquiry-stats"],
    queryFn: async () => {
      const { data } = await supabase
        .from("contact_messages")
        .select("car_id, created_at")
        .not("car_id", "is", null)
        .order("created_at", { ascending: false });
      const stats: Record<string, { count: number; latest: string }> = {};
      for (const row of data || []) {
        if (!row.car_id) continue;
        const existing = stats[row.car_id];
        if (existing) {
          existing.count += 1;
        } else {
          stats[row.car_id] = { count: 1, latest: row.created_at };
        }
      }
      return stats;
    },
  });
  // Backwards-compatible counts map for the sort logic below
  const inquiryCounts = useMemo(() => {
    if (!inquiryStats) return undefined;
    const out: Record<string, number> = {};
    for (const [id, s] of Object.entries(inquiryStats)) out[id] = s.count;
    return out;
  }, [inquiryStats]);

  const saveMutation = useMutation({
    mutationFn: async (data: CarForm) => {
      const payload = {
        brand: data.brand, model: data.model, variant: data.variant || null, year: data.year,
        mileage: data.mileage, price: data.price, fuel_type: data.fuel_type as any,
        transmission: data.transmission as any, color: data.color || null, body_type: data.body_type || null,
        doors: data.doors, seats: data.seats, engine_size: data.engine_size || null,
        horsepower: data.horsepower || null, description: data.description || null,
        equipment: data.equipment ? data.equipment.split("\n").filter(Boolean) : [],
        images: data.images.length > 0 ? data.images : [],
        status: data.status as any, is_new: data.is_new, is_popular: data.is_popular,
        is_leasing: data.is_leasing, is_demo: data.is_demo, leasing_price: data.leasing_price || null,
        first_registration: data.first_registration || null,
        seo_title: data.seo_title || null,
        seo_description: data.seo_description || null,
        seo_slug: data.seo_slug || `${data.brand}-${data.model}-${Date.now()}`.toLowerCase().replace(/\s+/g, "-"),
      };

      let carId: string | null = editId;
      if (editId) {
        const { error } = await supabase.from("cars").update(payload).eq("id", editId);
        if (error) throw error;
      } else {
        const { data: inserted, error } = await supabase.from("cars").insert(payload).select("id").single();
        if (error) throw error;
        carId = inserted?.id ?? null;
      }

      const vin = data.vin_number?.trim() || null;
      const plate = data.license_plate?.trim() || null;
      // GTIN: only persist when it's a valid 8/12/13/14-digit numeric string.
      // Anything else is silently dropped so we never ship garbage to Meta/Google.
      const rawGtin = data.gtin?.trim() || "";
      const gtin = /^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test(rawGtin) ? rawGtin : null;
      if (carId && (vin || plate || gtin)) {
        const { error: idErr } = await supabase
          .from("car_identifiers")
          .upsert({ car_id: carId, vin_number: vin, license_plate: plate, gtin }, { onConflict: "car_id" });
        if (idErr) throw idErr;
      } else if (carId) {
        await supabase.from("car_identifiers").delete().eq("car_id", carId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      setDialogOpen(false);
      setEditId(null);
      setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("cars").delete().eq("id", id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-cars"] }),
  });

  // Quick-toggle is_demo without opening the full edit dialog.
  const toggleDemoMutation = useMutation({
    mutationFn: async ({ id, isDemo }: { id: string; isDemo: boolean }) => {
      const { error } = await supabase.from("cars").update({ is_demo: isDemo }).eq("id", id);
      if (error) throw error;
      return isDemo;
    },
    onSuccess: (isDemo) => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      toast.success(isDemo ? "Markeret som demo" : "Demo-markering fjernet");
    },
    onError: (err: any) => toast.error(err?.message || "Kunne ikke opdatere demo-status"),
  });

  // Quick-cycle status: active → reserved → sold → draft → active. Lets admins
  // flip a car's listing-state directly from the table without opening the
  // full edit dialog. After every change we ping Google/Bing + warm the public
  // feeds so search engines pick the change up within minutes.
  const statusLabels: Record<string, string> = { active: "Aktiv", draft: "Kladde", sold: "Solgt", reserved: "Reserveret" };
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("cars").update({ status }).eq("id", id);
      if (error) throw error;
      // Fire ping in the background — UI must not wait for Google/Bing.
      // Failures are surfaced as a soft toast but never block the status flip.
      pingStatusFn({ data: { carId: id, status: status as "active" | "draft" | "sold" | "reserved" } })
        .then((res) => {
          const okCount = res.results.filter((r) => r.ok).length;
          if (okCount > 0) {
            toast.success(`Søgemaskiner notificeret (${okCount}/${res.results.length})`, {
              description: "Google/Bing har modtaget sitemap-ping. Feeds er varmet op.",
            });
          }
        })
        .catch((e) => {
          console.warn("Status ping failed:", e);
        });
      return status;
    },
    onSuccess: (status) => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      toast.success(`Status ændret til ${statusLabels[status] ?? status}`);
    },
    onError: (err: any) => toast.error(err?.message || "Kunne ikke opdatere status"),
  });

  // Convert a Bilinfo-synced car to a manual car so the hourly sync no longer
  // overwrites it. We clear bilinfo_id too so a future re-sync of the same
  // listing creates a new row instead of silently re-attaching.
  const convertToManualMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("cars")
        .update({ source: "manual", bilinfo_id: null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      setConvertTarget(null);
      toast.success("Bilen er nu manuel og bliver ikke overskrevet af Bilinfo-syncen");
    },
    onError: (err: any) => toast.error(err?.message || "Kunne ikke konvertere bilen"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("cars").delete().in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      toast.success(`${count} ${count === 1 ? "bil" : "biler"} slettet`);
    },
    onError: (err: any) => toast.error(err?.message || "Kunne ikke slette biler"),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: "active" | "draft" | "sold" | "reserved" }) => {
      const { error } = await supabase.from("cars").update({ status }).in("id", ids);
      if (error) throw error;
      // Fan out pings in the background — one per car so each detail URL is
      // recrawled. Capped per-call by the 4s timeout in the server fn.
      Promise.allSettled(
        ids.map((id) => pingStatusFn({ data: { carId: id, status } })),
      ).catch(() => {});
      return { count: ids.length, status };
    },
    onSuccess: ({ count, status }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      setSelectedIds(new Set());
      const label = statusLabels[status] ?? status;
      toast.success(`${count} ${count === 1 ? "bil" : "biler"} sat til ${label}`);
    },
    onError: (err: any) => toast.error(err?.message || "Kunne ikke opdatere status"),
  });

  const csvImportMutation = useMutation({
    mutationFn: async (csv: string) => {
      const lines = csv.trim().split("\n");
      if (lines.length < 2) throw new Error("CSV must have header + data");
      const headers = lines[0].split(";").map((h) => h.trim().toLowerCase());
      const rows = lines.slice(1).map((line) => {
        const vals = line.split(";");
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => { obj[h] = vals[i]?.trim() || ""; });
        return obj;
      });

      for (const row of rows) {
        await supabase.from("cars").insert({
          brand: row.brand || row.mærke || "",
          model: row.model || "",
          variant: row.variant || null,
          year: parseInt(row.year || row.modelår || "2024"),
          mileage: parseInt(row.mileage || row.kilometer || "0"),
          price: parseInt(row.price || row.pris || "0"),
          fuel_type: (row.fuel_type || row.brændstof || "benzin") as any,
          transmission: (row.transmission || row.gearkasse || "automatik") as any,
          color: row.color || row.farve || null,
          status: "draft" as any,
          seo_slug: `${row.brand || row.mærke}-${row.model}-${Date.now()}`.toLowerCase().replace(/\s+/g, "-"),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-cars"] });
      setCsvDialogOpen(false);
      setCsvText("");
    },
  });

  const openEdit = (car: any) => {
    setEditId(car.id);
    setForm({
      brand: car.brand, model: car.model, variant: car.variant || "", year: car.year,
      mileage: car.mileage, price: car.price, fuel_type: car.fuel_type,
      transmission: car.transmission, color: car.color || "", body_type: car.body_type || "",
      doors: car.doors || 4, seats: car.seats || 5, engine_size: car.engine_size || "",
      horsepower: car.horsepower || 0, description: car.description || "",
      equipment: (car.equipment || []).join("\n"), status: car.status,
      is_new: car.is_new || false, is_popular: car.is_popular || false,
      is_leasing: car.is_leasing || false, is_demo: car.is_demo || false, leasing_price: car.leasing_price || 0,
      first_registration: car.first_registration || "", vin_number: car.vin_number || "",
      license_plate: car.license_plate || "", gtin: (car as { gtin?: string | null }).gtin || "",
      seo_title: car.seo_title || "",
      seo_description: car.seo_description || "", seo_slug: car.seo_slug || "",
      images: car.images || [],
    });
    setDialogOpen(true);
  };

  // Deep-link support: if the URL contains ?editId=<uuid> (e.g. from the
  // image-health report on /admin/sync), open the edit dialog for that car
  // as soon as it appears in the loaded list. We then strip the param so a
  // browser refresh does not keep re-opening the dialog.
  const editIdParam = urlSearch.editId;
  useEffect(() => {
    if (!editIdParam || !cars || dialogOpen) return;
    const car = (cars as any[]).find((c) => c.id === editIdParam);
    if (!car) return;
    openEdit(car);
    navigate({
      search: (prev: SearchState) => {
        const { editId: _drop, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIdParam, cars]);

  const handleGenerateDescription = async () => {
    if (!form.brand || !form.model || !form.year) {
      toast.error("Udfyld mærke, model og årgang først");
      return;
    }
    setGeneratingDesc(true);
    try {
      const result = await generateDescFn({
        data: {
          brand: form.brand,
          model: form.model,
          variant: form.variant,
          year: form.year,
          mileage: form.mileage,
          fuel_type: form.fuel_type,
          transmission: form.transmission,
          color: form.color,
          body_type: form.body_type,
          doors: form.doors,
          seats: form.seats,
          engine_size: form.engine_size,
          horsepower: form.horsepower,
          equipment: form.equipment,
          tone: descTone,
        },
      });
      if (result.error) {
        toast.error(result.error);
      } else if (result.description) {
        setForm((prev) => ({
          ...prev,
          description: result.description!,
          seo_title: prev.seo_title || result.seo_title || "",
          seo_description: prev.seo_description || result.seo_description || "",
        }));
        toast.success("Beskrivelse genereret med AI");
      }
    } catch (err) {
      console.error(err);
      toast.error("Kunne ikke generere beskrivelse");
    } finally {
      setGeneratingDesc(false);
    }
  };

  const statusColors: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    draft: "bg-yellow-100 text-yellow-800",
    sold: "bg-red-100 text-red-800",
    reserved: "bg-blue-100 text-blue-800",
  };

  const hasImages = (car: any) => Array.isArray(car?.images) && car.images.length > 0;

  const filteredCars = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (cars || []).filter((car) => {
      if (statusFilter !== "all" && car.status !== statusFilter) return false;
      if (sourceFilter !== "all" && (car as any).source !== sourceFilter) return false;
      if (demoFilter === "demo" && !(car as any).is_demo) return false;
      if (demoFilter === "real" && (car as any).is_demo) return false;
      if (imagesFilter === "with" && !hasImages(car)) return false;
      if (imagesFilter === "without" && hasImages(car)) return false;
      if (!q) return true;
      const haystack = `${car.brand} ${car.model} ${car.variant ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
    if (!sort) return filtered;
    const statusOrder: Record<string, number> = { active: 0, draft: 1, sold: 2 };
    const sorted = [...filtered].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sort.key === "brand") {
        av = `${a.brand} ${a.model}`.toLowerCase();
        bv = `${b.brand} ${b.model}`.toLowerCase();
      } else if (sort.key === "status") {
        av = statusOrder[a.status] ?? 99;
        bv = statusOrder[b.status] ?? 99;
      } else if (sort.key === "inquiries") {
        av = inquiryCounts?.[a.id] ?? 0;
        bv = inquiryCounts?.[b.id] ?? 0;
      } else {
        av = (a as unknown as Record<string, number>)[sort.key] ?? 0;
        bv = (b as unknown as Record<string, number>)[sort.key] ?? 0;
      }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [cars, search, statusFilter, sourceFilter, demoFilter, imagesFilter, sort, inquiryCounts]);

  // Counts for filter badges. Each filter group counts cars matching the
  // *other* active filter so toggling between options shows accurate numbers.
  const statusCounts = useMemo(() => {
    const base = (cars || []).filter((c) => {
      if (sourceFilter !== "all" && (c as any).source !== sourceFilter) return false;
      if (demoFilter === "demo" && !(c as any).is_demo) return false;
      if (demoFilter === "real" && (c as any).is_demo) return false;
      if (imagesFilter === "with" && !hasImages(c)) return false;
      if (imagesFilter === "without" && hasImages(c)) return false;
      return true;
    });
    return {
      all: base.length,
      active: base.filter((c) => c.status === "active").length,
      draft: base.filter((c) => c.status === "draft").length,
      sold: base.filter((c) => c.status === "sold").length,
      reserved: base.filter((c) => c.status === "reserved").length,
    };
  }, [cars, sourceFilter, demoFilter, imagesFilter]);
  const sourceCounts = useMemo(() => {
    const base = (cars || []).filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (demoFilter === "demo" && !(c as any).is_demo) return false;
      if (demoFilter === "real" && (c as any).is_demo) return false;
      if (imagesFilter === "with" && !hasImages(c)) return false;
      if (imagesFilter === "without" && hasImages(c)) return false;
      return true;
    });
    return {
      all: base.length,
      manual: base.filter((c) => (c as any).source === "manual").length,
      bilinfo: base.filter((c) => (c as any).source === "bilinfo").length,
    };
  }, [cars, statusFilter, demoFilter, imagesFilter]);
  const demoCounts = useMemo(() => {
    const base = (cars || []).filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (sourceFilter !== "all" && (c as any).source !== sourceFilter) return false;
      if (imagesFilter === "with" && !hasImages(c)) return false;
      if (imagesFilter === "without" && hasImages(c)) return false;
      return true;
    });
    return {
      all: base.length,
      demo: base.filter((c) => (c as any).is_demo).length,
      real: base.filter((c) => !(c as any).is_demo).length,
    };
  }, [cars, statusFilter, sourceFilter, imagesFilter]);
  const imagesCounts = useMemo(() => {
    const base = (cars || []).filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (sourceFilter !== "all" && (c as any).source !== sourceFilter) return false;
      if (demoFilter === "demo" && !(c as any).is_demo) return false;
      if (demoFilter === "real" && (c as any).is_demo) return false;
      return true;
    });
    return {
      all: base.length,
      with: base.filter((c) => hasImages(c)).length,
      without: base.filter((c) => !hasImages(c)).length,
    };
  }, [cars, statusFilter, sourceFilter, demoFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCars.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedCars = filteredCars.slice(pageStart, pageStart + PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sourceFilter, demoFilter, imagesFilter, sort]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Biler</h1>
          <p className="mt-1 text-sm text-muted-foreground">Administrer bilerne i dit lager</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkImagesOpen(true)}>
            <ImagePlus size={16} className="mr-2" /> Bulk-billeder
          </Button>
          <Button variant="outline" onClick={() => setCsvDialogOpen(true)}>
            <Upload size={16} className="mr-2" /> Import CSV
          </Button>
          <Button onClick={() => { setEditId(null); setForm(emptyForm); setDialogOpen(true); }} className="bg-accent text-accent-foreground hover:opacity-90">
            <Plus size={16} className="mr-2" /> Tilføj bil
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg på mærke, model eller variant..."
            className="pr-8"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title="Ryd søgning"
              aria-label="Ryd søgning"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-1 rounded-md border border-border p-1">
          {(["all", "active", "reserved", "draft", "sold"] as const).map((s) => {
            const count = statusCounts[s];
            const isActive = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {s === "all" ? "Alle" : s === "active" ? "Aktive" : s === "draft" ? "Kladder" : s === "reserved" ? "Reserveret" : "Solgte"}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                    isActive
                      ? "bg-accent-foreground/15 text-accent-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-1 rounded-md border border-border p-1">
          {(["all", "manual", "bilinfo"] as const).map((s) => {
            const count = sourceCounts[s];
            const isActive = sourceFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setSourceFilter(s)}
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title={
                  s === "all"
                    ? "Vis alle kilder"
                    : s === "manual"
                      ? "Vis kun biler oprettet manuelt"
                      : "Vis kun biler synkroniseret fra Bilinfo"
                }
              >
                {s === "all" ? "Alle kilder" : s === "manual" ? "Manuelle" : "Bilinfo"}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                    isActive
                      ? "bg-accent-foreground/15 text-accent-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-1 rounded-md border border-border p-1">
          {(["all", "real", "demo"] as const).map((s) => {
            const count = demoCounts[s];
            const isActive = demoFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setDemoFilter(s)}
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title={
                  s === "all"
                    ? "Vis både rigtige og demo-biler"
                    : s === "real"
                      ? "Skjul demo-biler"
                      : "Vis kun demo-biler"
                }
              >
                {s === "all" ? "Alle" : s === "real" ? "Rigtige" : "Demo"}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                    isActive
                      ? "bg-accent-foreground/15 text-accent-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-1 rounded-md border border-border p-1">
          {(["all", "with", "without"] as const).map((s) => {
            const count = imagesCounts[s];
            const isActive = imagesFilter === s;
            const needsAttention = s === "without" && count > 0;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setImagesFilter(s)}
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium transition ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : needsAttention
                      ? "text-amber-700 hover:text-amber-900"
                      : "text-muted-foreground hover:text-foreground"
                }`}
                title={
                  s === "all"
                    ? "Vis biler uanset om de har billeder"
                    : s === "with"
                      ? "Vis kun biler med mindst ét billede"
                      : "Vis biler der mangler billeder — upload fotos så de ikke vises som tomme kort"
                }
              >
                {s === "all" ? "Alle billeder" : s === "with" ? "Med billeder" : "Uden billeder"}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                    isActive
                      ? "bg-accent-foreground/15 text-accent-foreground"
                      : needsAttention
                        ? "bg-amber-100 text-amber-800"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mt-4 flex flex-col gap-3 rounded-md border border-accent/40 bg-accent/10 p-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-foreground">
            {selectedIds.size} {selectedIds.size === 1 ? "bil" : "biler"} valgt
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Sæt status:</span>
            {(["active", "reserved", "draft", "sold"] as const).map((s) => (
              <button
                key={s}
                type="button"
                disabled={bulkStatusMutation.isPending}
                onClick={() =>
                  bulkStatusMutation.mutate({ ids: Array.from(selectedIds), status: s })
                }
                className="inline-flex items-center rounded-md border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent disabled:opacity-50"
              >
                {statusLabels[s]}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setBulkDeleteOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:border-destructive hover:text-destructive disabled:opacity-50"
            >
              <Trash2 size={12} /> Slet valgte
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex items-center rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Ryd valg
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="mt-8 overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="w-10 px-4 py-3">
                <Checkbox
                  checked={
                    pagedCars.length > 0 && pagedCars.every((c) => selectedIds.has(c.id))
                      ? true
                      : pagedCars.some((c) => selectedIds.has(c.id))
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(checked) => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) pagedCars.forEach((c) => next.add(c.id));
                      else pagedCars.forEach((c) => next.delete(c.id));
                      return next;
                    });
                  }}
                  aria-label="Vælg alle på siden"
                />
              </th>
              {([
                { key: "brand" as const, label: "Bil" },
                { key: "year" as const, label: "År" },
                { key: "price" as const, label: "Pris" },
                { key: "status" as const, label: "Status" },
              ]).map((col) => {
                const active = sort?.key === col.key;
                const Icon = active ? (sort!.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th key={col.key} className="px-4 py-3 text-left font-medium text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className={`inline-flex items-center gap-1 transition-colors hover:text-foreground ${active ? "text-foreground" : ""}`}
                      aria-label={`Sortér efter ${col.label}`}
                    >
                      {col.label}
                      <Icon size={12} className={active ? "" : "opacity-40"} />
                    </button>
                  </th>
                );
              })}
              {(() => {
                const active = sort?.key === "inquiries";
                const Icon = active ? (sort!.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    <button
                      type="button"
                      onClick={() => toggleSort("inquiries")}
                      className={`inline-flex items-center gap-1 transition-colors hover:text-foreground ${active ? "text-foreground" : ""}`}
                      aria-label="Sortér efter henvendelser"
                    >
                      Henvendelser
                      <Icon size={12} className={active ? "" : "opacity-40"} />
                    </button>
                  </th>
                );
              })()}
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Indlæser...</td></tr>
            ) : pagedCars.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                {(cars?.length ?? 0) === 0 ? "Ingen biler endnu" : "Ingen biler matcher filteret"}
              </td></tr>
            ) : (
              pagedCars.map((car) => (
                <tr
                  key={car.id}
                  className={`border-t border-border transition-colors hover:bg-accent/10 data-[selected=true]:bg-accent/10 ${
                    car.source === "bilinfo"
                      ? "border-l-4 border-l-blue-500 bg-blue-50/40"
                      : "border-l-4 border-l-transparent"
                  }`}
                  data-selected={selectedIds.has(car.id)}
                  title={car.source === "bilinfo" ? "Synkroniseret fra Bilinfo" : "Manuelt oprettet"}
                >
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedIds.has(car.id)}
                      onCheckedChange={(checked) => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(car.id);
                          else next.delete(car.id);
                          return next;
                        });
                      }}
                      aria-label={`Vælg ${car.brand} ${car.model}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground">
                    <div className="flex items-center gap-2">
                      <span>{car.brand} {car.model} {car.variant}</span>
                      {car.source === "bilinfo" && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800"
                          title="Synkroniseret fra Bilinfo — opdateres automatisk hver time"
                        >
                          <RefreshCw size={10} /> Bilinfo
                        </span>
                      )}
                      {(car as any).is_demo && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                          title="Test-data — kan slettes når der kommer rigtige biler ind"
                        >
                          Demo
                        </span>
                      )}
                      {(() => {
                        const imageCount = (car.images ?? []).length;
                        const tone =
                          imageCount === 0
                            ? "bg-red-100 text-red-800 hover:bg-red-200"
                            : imageCount < 4
                              ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                              : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";
                        const title =
                          imageCount === 0
                            ? "Ingen billeder uploadet — klik for at tilføje fotos"
                            : imageCount < 4
                              ? `Kun ${imageCount} billede${imageCount === 1 ? "" : "r"} — klik for at tilføje flere`
                              : `${imageCount} billeder — klik for at redigere`;
                        return (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(car);
                              // Wait for dialog to mount, then scroll image section into view
                              setTimeout(() => {
                                document
                                  .getElementById("car-images-section")
                                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
                              }, 150);
                            }}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${tone}`}
                            title={title}
                          >
                            <ImagePlus size={10} /> {imageCount}
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{car.year}</td>
                  <td className="px-4 py-3 text-muted-foreground">DKK {new Intl.NumberFormat("da-DK").format(car.price)},-</td>
                  <td className="px-4 py-3">
                    {(() => {
                      const cycle: Record<string, string> = { active: "reserved", reserved: "sold", sold: "draft", draft: "active" };
                      const next = cycle[car.status] ?? "active";
                      return (
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: car.id, status: next })}
                          disabled={updateStatusMutation.isPending}
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50 ${statusColors[car.status] || ""}`}
                          title={`Klik for at skifte til ${statusLabels[next]}`}
                        >
                          {statusLabels[car.status] ?? car.status}
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const stat = inquiryStats?.[car.id];
                      const count = stat?.count ?? 0;
                      if (count === 0) {
                        return <span className="text-xs text-muted-foreground">—</span>;
                      }
                      const latestMs = stat ? Date.now() - new Date(stat.latest).getTime() : 0;
                      const latestDays = Math.floor(latestMs / (1000 * 60 * 60 * 24));
                      const latestLabel =
                        latestDays === 0
                          ? "i dag"
                          : latestDays === 1
                            ? "i går"
                            : `for ${latestDays} dage siden`;
                      const isStale = latestDays > 30;
                      const carLabel = `${car.brand} ${car.model}${car.variant ? ` ${car.variant}` : ""}`.trim();
                      return (
                        <Link
                          to="/admin/beskeder"
                          search={{ q: carLabel, status: "all", page: 1, pageSize: 20 } as any}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                            isStale
                              ? "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                              : "bg-accent/15 text-accent hover:bg-accent hover:text-accent-foreground"
                          }`}
                          title={`${count} ${count === 1 ? "henvendelse" : "henvendelser"} · sidste ${latestLabel} — klik for at se`}
                        >
                          <Mail size={12} />
                          {count}
                        </Link>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {car.source === "bilinfo" && (
                        <button
                          onClick={() =>
                            setConvertTarget({
                              id: car.id,
                              label: `${car.brand} ${car.model} ${car.variant ?? ""}`.trim(),
                            })
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent"
                          title="Konvertér til manuel — bilen bliver ikke længere overskrevet af Bilinfo-syncen"
                        >
                          <Unlink size={12} /> Manuel
                        </button>
                      )}
                      <button
                        onClick={() => toggleDemoMutation.mutate({ id: car.id, isDemo: !(car as any).is_demo })}
                        disabled={toggleDemoMutation.isPending}
                        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-50 ${
                          (car as any).is_demo
                            ? "border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200"
                            : "border-border text-muted-foreground hover:border-amber-400 hover:text-amber-700"
                        }`}
                        title={(car as any).is_demo ? "Fjern demo-markering" : "Marker som demo"}
                      >
                        Demo
                      </button>
                      <button
                        onClick={() => openEdit(car)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent"
                        title="Rediger bil"
                      >
                        <Pencil size={12} /> Rediger
                      </button>
                      <button
                        onClick={() =>
                          setDeleteTarget({
                            id: car.id,
                            label: `${car.brand} ${car.model} ${car.variant ?? ""}`.trim(),
                          })
                        }
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-destructive hover:text-destructive"
                        title="Slet bil"
                      >
                        <Trash2 size={12} /> Slet
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && filteredCars.length > 0 && (
        <div className="mt-4 flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-muted-foreground">
              Viser {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredCars.length)} af {filteredCars.length} biler
            </p>
            <label className="flex items-center gap-2 text-muted-foreground">
              <span>Pr. side:</span>
              <select
                value={PAGE_SIZE}
                onChange={(e) => setPageSize(Number(e.target.value) as 10 | 20 | 50 | 100)}
                className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground focus:border-accent focus:outline-none"
                aria-label="Antal biler pr. side"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-muted-foreground"
                aria-label="Forrige side"
              >
                <ChevronLeft size={14} /> Forrige
              </button>
              <span className="px-2 text-muted-foreground">
                Side {currentPage} af {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-muted-foreground"
                aria-label="Næste side"
              >
                Næste <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Car form dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Rediger bil" : "Tilføj ny bil"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-6">
            {/* Basic info */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Grundlæggende info</h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <div><label className="mb-1 block text-xs text-muted-foreground">Mærke *</label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} required /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Model *</label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} required /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Variant</label><Input value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} /></div>
              </div>
            </div>

            {/* Specs */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Specifikationer</h3>
              <div className="grid gap-4 sm:grid-cols-4">
                <div><label className="mb-1 block text-xs text-muted-foreground">Modelår *</label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })} required /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Kilometer *</label><Input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: parseInt(e.target.value) })} required /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Pris (DKK) *</label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) })} required /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">HK</label><Input type="number" value={form.horsepower} onChange={(e) => setForm({ ...form, horsepower: parseInt(e.target.value) })} /></div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Brændstof</label>
                  <select value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="benzin">Benzin</option><option value="diesel">Diesel</option><option value="el">El</option><option value="hybrid">Hybrid</option><option value="plugin_hybrid">Plugin Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Gearkasse</label>
                  <select value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="automatik">Automatik</option><option value="manuel">Manuel</option>
                  </select>
                </div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Farve</label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Karosseri</label><Input value={form.body_type} onChange={(e) => setForm({ ...form, body_type: e.target.value })} /></div>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-4">
                <div><label className="mb-1 block text-xs text-muted-foreground">Døre</label><Input type="number" value={form.doors} onChange={(e) => setForm({ ...form, doors: parseInt(e.target.value) })} /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Sæder</label><Input type="number" value={form.seats} onChange={(e) => setForm({ ...form, seats: parseInt(e.target.value) })} /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Motor</label><Input value={form.engine_size} onChange={(e) => setForm({ ...form, engine_size: e.target.value })} /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">1. reg. dato</label><Input type="date" value={form.first_registration} onChange={(e) => setForm({ ...form, first_registration: e.target.value })} /></div>
              </div>
            </div>

            {/* Status & flags */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Status & synlighed</h3>
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="draft">Kladde</option><option value="active">Aktiv</option><option value="reserved">Reserveret</option><option value="sold">Solgt</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} /> <span className="text-sm">Nyhed</span></label>
                <label className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.is_popular} onChange={(e) => setForm({ ...form, is_popular: e.target.checked })} /> <span className="text-sm">Populær</span></label>
                <label className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.is_leasing} onChange={(e) => setForm({ ...form, is_leasing: e.target.checked })} /> <span className="text-sm">Leasing</span></label>
                <label className="flex items-center gap-2 pt-5" title="Marker som test/demo-bil — vises ikke som rigtig lagerbeholdning"><input type="checkbox" checked={form.is_demo} onChange={(e) => setForm({ ...form, is_demo: e.target.checked })} /> <span className="text-sm">Demo</span></label>
              </div>
              {form.is_leasing && (
                <div className="mt-4 max-w-xs">
                  <label className="mb-1 block text-xs text-muted-foreground">Leasing pris/md</label>
                  <Input type="number" value={form.leasing_price} onChange={(e) => setForm({ ...form, leasing_price: parseInt(e.target.value) })} />
                </div>
              )}
            </div>

            {/* Description & equipment */}
            <div>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">Beskrivelse & udstyr</h3>
                <div className="flex items-center gap-2">
                  <select
                    value={descTone}
                    onChange={(e) => setDescTone(e.target.value as "sælgende" | "neutral" | "luksus")}
                    className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                  >
                    <option value="sælgende">Sælgende tone</option>
                    <option value="neutral">Neutral tone</option>
                    <option value="luksus">Luksus tone</option>
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleGenerateDescription}
                    disabled={generatingDesc}
                    className="gap-1.5"
                  >
                    {generatingDesc ? (
                      <><Loader2 size={14} className="animate-spin" /> Genererer...</>
                    ) : (
                      <><Sparkles size={14} /> Generér med AI</>
                    )}
                  </Button>
                </div>
              </div>
              <Textarea placeholder="Beskrivelse af bilen..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={6} />
              <p className="mt-1 text-xs text-muted-foreground">AI bruger udstyrslisten herunder + bilens specs til at skrive en sælgende beskrivelse og udfylde SEO-felter automatisk.</p>
              <div className="mt-4">
                <label className="mb-1 block text-xs text-muted-foreground">Udstyr (en per linje)</label>
                <Textarea placeholder="Fartpilot&#10;Bakkamera&#10;Navigation&#10;..." value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} rows={6} />
              </div>
            </div>

            {/* Images */}
            <div id="car-images-section">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Billeder</h3>
              
              {/* Image preview grid */}
              {form.images.length > 0 && (
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {form.images.map((url, i) => (
                    <div key={i} className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-muted">
                      <img src={url} alt={`Billede ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      <div className="absolute inset-0 flex items-start justify-between bg-black/0 p-1.5 opacity-0 transition-all group-hover:bg-black/40 group-hover:opacity-100">
                        <div className="flex gap-1">
                          {i > 0 && (
                            <button type="button" onClick={() => moveImage(i, i - 1)} className="rounded bg-white/90 px-1.5 py-0.5 text-xs font-medium text-foreground hover:bg-white" title="Flyt til venstre">←</button>
                          )}
                          {i < form.images.length - 1 && (
                            <button type="button" onClick={() => moveImage(i, i + 1)} className="rounded bg-white/90 px-1.5 py-0.5 text-xs font-medium text-foreground hover:bg-white" title="Flyt til højre">→</button>
                          )}
                        </div>
                        <button type="button" onClick={() => removeImage(i)} className="rounded-full bg-destructive p-1 text-destructive-foreground hover:opacity-80" title="Fjern billede">
                          <X size={12} />
                        </button>
                      </div>
                      {i === 0 && (
                        <span className="absolute bottom-1.5 left-1.5 rounded bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">Forside</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload area */}
              {!editId && (
                <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-50/40 p-3 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
                  Gem bilen først for at uploade billeder. Pipelinen genererer AVIF/WebP/JPG @ 960/1280/1920 og skriver feed-klar manifest.
                </div>
              )}
              <div
                className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-8 transition-colors ${editId ? "cursor-pointer hover:border-accent hover:bg-accent/5" : "cursor-not-allowed opacity-50"}`}
                onClick={() => editId && fileInputRef.current?.click()}
                onDragOver={(e) => {
                  if (!editId) return;
                  e.preventDefault();
                  e.currentTarget.classList.add("border-accent", "bg-accent/5");
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove("border-accent", "bg-accent/5")}
                onDrop={(e) => {
                  if (!editId) return;
                  e.preventDefault();
                  e.currentTarget.classList.remove("border-accent", "bg-accent/5");
                  if (e.dataTransfer.files.length > 0) uploadImages(e.dataTransfer.files);
                }}
              >
                <ImagePlus size={32} className="text-muted-foreground" />
                <p className="mt-2 text-sm font-medium text-foreground">
                  {uploading
                    ? uploadProgress
                      ? `Genererer ${uploadProgress.derivative} (${uploadProgress.fileIndex}/${uploadProgress.fileTotal})…`
                      : "Uploader…"
                    : "Klik eller træk billeder hertil"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  JPG, PNG, WebP eller AVIF — maks 16 MB. Genererer 9 derivater (AVIF/WebP/JPG @ 960/1280/1920) per billede.
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                multiple
                className="hidden"
                onChange={(e) => { if (e.target.files) uploadImages(e.target.files); e.target.value = ""; }}
              />
              <CarImageManifestStatus carId={editId} imageCount={form.images.length} />
            </div>

            {/* IDs */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Identifikation</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="mb-1 block text-xs text-muted-foreground">Stelnummer (VIN)</label><Input value={form.vin_number} onChange={(e) => setForm({ ...form, vin_number: e.target.value })} /></div>
                <div><label className="mb-1 block text-xs text-muted-foreground">Nummerplade</label><Input value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} /></div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs text-muted-foreground">
                    GTIN / EAN / UPC (valgfri — 8, 12, 13 eller 14 cifre)
                  </label>
                  <Input
                    value={form.gtin}
                    onChange={(e) => setForm({ ...form, gtin: e.target.value.replace(/\D/g, "") })}
                    placeholder="fx 4012345678901"
                    inputMode="numeric"
                    pattern="\d*"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Bruges i Meta/Google produktfeeds. Ugyldige værdier ignoreres ved gem.
                  </p>
                </div>
              </div>
            </div>

            {/* SEO — preview, edit, validate per Google's length limits before publishing */}
            <div>
              <CarSeoEditor
                value={form}
                onChange={(seo) =>
                  setForm({
                    ...form,
                    seo_title: seo.seo_title,
                    seo_description: seo.seo_description,
                    seo_slug: seo.seo_slug,
                  })
                }
                onValidationChange={setSeoValidation}
              />
            </div>

            {/* AIA pre-publish checklist — surfaces every field that the
                Meta feed (/feeds/facebook) requires or remaps. Errors block
                publishing to "active"; warnings only degrade ad targeting. */}
            <div>
              <AiaChecklist
                car={{
                  brand: form.brand,
                  model: form.model,
                  variant: form.variant,
                  year: form.year,
                  mileage: form.mileage,
                  price: form.price,
                  fuel_type: form.fuel_type,
                  transmission: form.transmission,
                  body_type: form.body_type,
                  color: form.color,
                  images: form.images,
                  vin_number: form.vin_number,
                  gtin: form.gtin,
                }}
                onResultChange={setAiaResult}
              />
            </div>

            <div className="flex flex-col items-end gap-2 pt-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
              {seoValidation.hasErrors && (
                <span className="mr-auto text-xs text-destructive">
                  Ret SEO-fejl ovenfor før du kan gemme
                </span>
              )}
              {form.status === "active" && aiaResult && aiaResult.errorCount > 0 && (
                <span className="mr-auto text-xs text-destructive">
                  AIA-tjeklisten har {aiaResult.errorCount} fejl — kan ikke
                  publiceres som aktiv. Skift status til kladde, eller ret felterne.
                </span>
              )}
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuller</Button>
              <Button
                type="submit"
                disabled={
                  saveMutation.isPending ||
                  seoValidation.hasErrors ||
                  (form.status === "active" && !!aiaResult && aiaResult.errorCount > 0)
                }
                className="bg-accent text-accent-foreground hover:opacity-90"
              >
                {saveMutation.isPending ? "Gemmer..." : editId ? "Opdater bil" : "Opret bil"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importer biler fra CSV</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Indsæt CSV-data med semikolon-separator. Headers: brand;model;variant;year;mileage;price;fuel_type;transmission;color
          </p>
          <Textarea placeholder="brand;model;variant;year;mileage;price;fuel_type&#10;Audi;A3;Sport;2022;50000;249900;benzin" value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={10} />
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setCsvDialogOpen(false)}>Annuller</Button>
            <Button onClick={() => csvImportMutation.mutate(csvText)} disabled={csvImportMutation.isPending} className="bg-accent text-accent-foreground hover:opacity-90">
              {csvImportMutation.isPending ? "Importerer..." : "Importer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet bil?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette <strong>{deleteTarget?.label}</strong>?
              Denne handling kan ikke fortrydes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Slet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!convertTarget} onOpenChange={(open) => !open && setConvertTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konvertér til manuel?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{convertTarget?.label}</strong> er pt. synkroniseret fra Bilinfo og bliver
              opdateret automatisk hver time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">Hvis du konverterer den til manuel:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Bilen bliver <strong>ikke</strong> længere overskrevet af Bilinfo-syncen.</li>
              <li>Den bliver <strong>ikke</strong> automatisk markeret solgt, hvis den fjernes fra Bilinfo.</li>
              <li>Du kan redigere alle felter frit fremover.</li>
            </ul>
            <p className="mt-3">Du kan ikke fortryde dette uden at oprette bilen i Bilinfo igen.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={convertToManualMutation.isPending}>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (convertTarget) convertToManualMutation.mutate(convertTarget.id);
              }}
              disabled={convertToManualMutation.isPending}
            >
              Konvertér
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slet {selectedIds.size} {selectedIds.size === 1 ? "bil" : "biler"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Du er ved at slette <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? "bil" : "biler"} permanent.
              Denne handling kan ikke fortrydes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteMutation.isPending}>Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              disabled={bulkDeleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Slet alle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkImageUploadDialog
        open={bulkImagesOpen}
        onOpenChange={setBulkImagesOpen}
        cars={(cars ?? []).map((c) => ({
          id: c.id,
          brand: c.brand,
          model: c.model,
          year: c.year,
          vin_number: (c as { vin_number?: string | null }).vin_number ?? null,
          images: c.images,
        }))}
      />
    </div>
  );
}
